/**
 * ADR-0016 — idle inventory health scan after business becomes available.
 * Enables storage safe mode on deviation (ADR-0012).
 */

import { getCurrentBusinessId } from '../../../utils/sharedCore';
import {
  runInventoryLedgerHealthScan,
  INVENTORY_LEDGER_SAFE_MODE_REASON
} from './runInventoryLedgerHealthScan';

const SCANNED = new Set();

/**
 * @param {{ businessId?: string, force?: boolean, timeoutMs?: number }} [options]
 * @returns {Promise<object|null>}
 */
export function scheduleInventoryLedgerHealthScan(options = {}) {
  const businessId =
    options.businessId || getCurrentBusinessId({ allowFallback: false });
  if (!businessId) return Promise.resolve(null);

  const key = String(businessId);
  if (!options.force && SCANNED.has(key)) {
    return Promise.resolve({ skipped: true, reason: 'already_scanned', businessId });
  }

  const timeoutMs = options.timeoutMs != null ? options.timeoutMs : 4500;

  return new Promise((resolve) => {
    const run = async () => {
      try {
        const result = await runInventoryLedgerHealthScan({
          businessId,
          enableSafeMode: options.enableSafeMode !== false,
          clearSafeModeWhenOk: options.clearSafeModeWhenOk === true
        });
        SCANNED.add(key);
        resolve(result);
      } catch (err) {
        resolve({
          ok: false,
          error: err?.message || String(err),
          businessId,
          reason: INVENTORY_LEDGER_SAFE_MODE_REASON
        });
      }
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => {
        void run();
      }, { timeout: timeoutMs });
    } else {
      setTimeout(() => {
        void run();
      }, Math.min(timeoutMs, 1200));
    }
  });
}

export function resetInventoryLedgerHealthScanCache() {
  SCANNED.clear();
}
